import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Button, Form, Input, InputNumber, Radio, Spin, Tag} from 'antd';
import {ExclamationCircleOutlined, LineChartOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import PropTypes from 'prop-types';

import DBModal from 'components/modal/modal';
import FileUploadList from 'components/fileUploadList';
import {
  FULFIL_OR_REJECT_SWAP_TRANSACTION,
  UPDATE_SWAP_TRANSACTION_RATE,
} from 'graphql/mutations/wallet';
import {GET_PUBLIC_EXCHANGE_RATES} from 'graphql/queries/wallet';
import {isDev} from 'utils/constants';
import moment from 'moment';

import styles from './swapTransactions.module.scss';
import {
  formatMoney,
  formatRate,
  formatRateValue,
  statusMeta,
  statusTagColor,
  symbolFor,
  userFullName,
} from 'utils/currencyFormatting';

const receiptBucket = isDev
  ? 'transaction-receipts-bucket-nonprod-02'
  : 'transaction-receipts-bucket-01';

/** The swap being acted on, repeated in both modals so context is never lost. */
const TransactionContext = ({details}) => (
  <div className={styles.contextCard}>
    <div className={styles.contextItem}>
      <span className={styles.contextLabel}>User</span>
      <span className={styles.contextValue}>
        {userFullName(details?.user) || '—'}
      </span>
    </div>
    <div className={styles.contextItem}>
      <span className={styles.contextLabel}>Pair</span>
      <span className={styles.contextValue}>
        {details?.sourceCurrency} → {details?.targetCurrency}
      </span>
    </div>
    <div className={styles.contextItem}>
      <span className={styles.contextLabel}>Amount</span>
      <span className={styles.contextValue}>
        {formatMoney(details?.amountPaid, details?.sourceCurrency)}
      </span>
    </div>
    <div className={styles.contextItem}>
      <span className={styles.contextLabel}>Current status</span>
      <span>
        <Tag
          className={styles.statusTag}
          color={statusTagColor(details?.status)}>
          {statusMeta(details?.status).label}
        </Tag>
      </span>
    </div>
  </div>
);

TransactionContext.propTypes = {details: PropTypes.object};

/**
 * Step 1 picks an outcome, step 2 restates it before anything is written —
 * fulfilling or rejecting a swap moves real money and cannot be undone here.
 */
const FulfilSwapContent = ({details, onDone, onCancel, refetch}) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState('form');
  const [values, setValues] = useState({});
  const outcome = Form.useWatch('status', form);

  const [fulfilOrRejectPendingSwapTransaction, {loading}] = useMutation(
    FULFIL_OR_REJECT_SWAP_TRANSACTION,
    {errorPolicy: 'all'},
  );

  useEffect(() => {
    form.setFieldsValue({files: details?.files ?? []});
  }, [details, form]);

  const handleSubmit = () => {
    fulfilOrRejectPendingSwapTransaction({
      variables: {
        swapTransactionId: details?.id,
        status: values?.status,
        ...(values?.reason && {reasonForFailure: values.reason}),
      },
    })
      .then(({data}) => {
        const response = data?.fulfilOrRejectPendingSwapTransaction;
        if (response?.__typename === 'Error') {
          toast.error(response.message);
          return;
        }
        toast.success(
          values?.status === 'SUCCESSFUL' ? 'Swap fulfilled' : 'Swap rejected',
        );
        refetch?.();
        onDone();
      })
      .catch(error => toast.error(error?.message ?? 'Something went wrong'));
  };

  if (step === 'confirm') {
    return (
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h1 className={styles.modalTitle}>
            {values?.status === 'SUCCESSFUL'
              ? 'Fulfil this swap?'
              : 'Reject this swap?'}
          </h1>
          <p className={styles.modalSubtitle}>
            Check the details below. This cannot be undone from here.
          </p>
        </div>

        <div className={styles.confirmList}>
          <div className={styles.confirmRow}>
            <span>User</span>
            <span className={styles.confirmValue}>
              {userFullName(details?.user) || '—'}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span>Amount</span>
            <span className={styles.confirmValue}>
              {formatMoney(details?.amountPaid, details?.sourceCurrency)} →{' '}
              {formatMoney(details?.amountReceived, details?.targetCurrency, {
                decimals: 2,
              })}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span>Status change</span>
            <span className={styles.confirmValue}>
              <Tag
                className={styles.statusTag}
                color={statusTagColor(details?.status)}>
                {statusMeta(details?.status).label}
              </Tag>
              →{' '}
              <Tag
                className={styles.statusTag}
                color={statusTagColor(values?.status)}>
                {statusMeta(values?.status).label}
              </Tag>
            </span>
          </div>
          {values?.reason && (
            <div className={styles.confirmRow}>
              <span>Reason</span>
              <span className={styles.confirmValue}>{values.reason}</span>
            </div>
          )}
        </div>

        <div className={styles.warning}>
          <ExclamationCircleOutlined />
          <span>
            {values?.status === 'SUCCESSFUL'
              ? 'The user will be credited in their target currency wallet.'
              : 'The user will be refunded and told why the swap failed.'}
          </span>
        </div>

        <div className={styles.modalFooter}>
          <Button onClick={() => setStep('form')}>Back</Button>
          <Button
            type="primary"
            danger={values?.status === 'FAILED'}
            className={
              values?.status === 'FAILED'
                ? styles.dangerButton
                : styles.primaryButton
            }
            loading={loading}
            onClick={handleSubmit}>
            {values?.status === 'SUCCESSFUL' ? 'Fulfil swap' : 'Reject swap'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalHeader}>
        <h1 className={styles.modalTitle}>Fulfil or reject swap</h1>
        <p className={styles.modalSubtitle}>
          Decide what happens to this pending swap.
        </p>
      </div>

      <TransactionContext details={details} />

      <Form
        form={form}
        layout="vertical"
        className={styles.modalForm}
        onFinish={submitted => {
          setValues(submitted);
          setStep('confirm');
        }}>
        <Form.Item
          name="status"
          label="Outcome"
          rules={[{required: true, message: 'Choose an outcome'}]}>
          <Radio.Group buttonStyle="solid">
            <Radio.Button value="SUCCESSFUL">Fulfil</Radio.Button>
            <Radio.Button value="FAILED">Reject</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {outcome === 'FAILED' && (
          <Form.Item
            name="reason"
            label="Reason for rejection"
            tooltip="Shown to the user, so write it in plain language."
            rules={[{required: true, message: 'A reason is required'}]}>
            <Input.TextArea
              rows={3}
              placeholder="e.g. The rate moved before we could settle this swap."
            />
          </Form.Item>
        )}

        <FileUploadList
          form={form}
          name="files"
          label="Supporting files (optional)"
          transactionId={details?.id}
          forType="SWAP"
          refetch={refetch}
          bucket={receiptBucket}
          required={false}
        />

        <div className={styles.modalFooter}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            className={styles.primaryButton}>
            Continue
          </Button>
        </div>
      </Form>
    </div>
  );
};

FulfilSwapContent.propTypes = {
  details: PropTypes.object,
  onDone: PropTypes.func,
  onCancel: PropTypes.func,
  refetch: PropTypes.func,
};

/**
 * Correcting the rate on a pending swap. The live market rate sits next to the
 * input so an operator can sanity-check the number before committing it.
 */
const UpdateRateContent = ({details, onDone, onCancel, refetch}) => {
  const [form] = Form.useForm();
  const [step, setStep] = useState('form');
  const [values, setValues] = useState({});

  const [updateSwapTransaction, {loading}] = useMutation(
    UPDATE_SWAP_TRANSACTION_RATE,
  );

  const {data: rateData, loading: loadingRates} = useQuery(
    GET_PUBLIC_EXCHANGE_RATES,
    {
      variables: {
        from: details?.sourceCurrency,
        to: [details?.targetCurrency],
      },
      skip: !details?.sourceCurrency || !details?.targetCurrency,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  );

  const marketRate = rateData?.getPublicFxExchangeRates?.to?.[0]?.rate;
  const lastUpdated = rateData?.getPublicFxExchangeRates?.lastUpdated;

  useEffect(() => {
    form.setFieldsValue({
      rate: details?.rate,
      files: details?.files ?? [],
    });
  }, [details, form]);

  const handleSubmit = () => {
    updateSwapTransaction({
      variables: {
        input: {
          swapTransactionId: details?.id,
          rate: Number(values?.rate),
          ...(values?.files?.length ? {files: values.files} : {}),
        },
      },
    })
      .then(({data}) => {
        const response = data?.updateSwapTransaction;
        if (response?.__typename === 'Error') {
          toast.error(response.message);
          return;
        }
        toast.success('Rate updated');
        refetch?.();
        onDone();
      })
      .catch(error => toast.error(error?.message ?? 'Something went wrong'));
  };

  if (step === 'confirm') {
    return (
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h1 className={styles.modalTitle}>Update the rate?</h1>
          <p className={styles.modalSubtitle}>
            The amount the user receives is recalculated with this rate.
          </p>
        </div>

        <div className={styles.confirmList}>
          <div className={styles.confirmRow}>
            <span>Pair</span>
            <span className={styles.confirmValue}>
              {details?.sourceCurrency} → {details?.targetCurrency}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span>Current rate</span>
            <span className={styles.confirmValue}>
              {formatRate(
                details?.rate,
                details?.sourceCurrency,
                details?.targetCurrency,
              )}
            </span>
          </div>
          <div className={styles.confirmRow}>
            <span>New rate</span>
            <span className={styles.confirmValue}>
              {formatRate(
                values?.rate,
                details?.sourceCurrency,
                details?.targetCurrency,
              )}
            </span>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <Button onClick={() => setStep('form')}>Back</Button>
          <Button
            type="primary"
            className={styles.primaryButton}
            loading={loading}
            onClick={handleSubmit}>
            Update rate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalHeader}>
        <h1 className={styles.modalTitle}>Update rate and files</h1>
        <p className={styles.modalSubtitle}>
          Set the rate this swap settles at, and attach any paperwork.
        </p>
      </div>

      <TransactionContext details={details} />

      {loadingRates ? (
        <div className={styles.marketRate}>
          <Spin size="small" />
          <span className={styles.marketRateHint}>Fetching market rate…</span>
        </div>
      ) : (
        marketRate != null && (
          <div className={styles.marketRate}>
            <LineChartOutlined />
            <div className={styles.marketRateBody}>
              <span className={styles.marketRateLabel}>Live market rate</span>
              <span className={styles.marketRateValue}>
                {formatRate(
                  marketRate,
                  details?.sourceCurrency,
                  details?.targetCurrency,
                )}
              </span>
              {lastUpdated && (
                <span className={styles.marketRateHint}>
                  Updated {moment(lastUpdated).format('DD MMM YYYY, h:mm A')}
                </span>
              )}
            </div>
          </div>
        )
      )}

      <Form
        form={form}
        layout="vertical"
        className={styles.modalForm}
        onFinish={submitted => {
          setValues(submitted);
          setStep('confirm');
        }}>
        <Form.Item
          name="rate"
          label={`Rate — ${details?.targetCurrency ?? ''} per 1 ${
            details?.sourceCurrency ?? ''
          }`}
          rules={[
            {required: true, message: 'Enter a rate'},
            {
              async validator(_, value) {
                if (value === undefined || value === null) return;
                if (Number(value) > 0) return;
                throw new Error('The rate must be above zero');
              },
            },
          ]}>
          <InputNumber
            className={styles.fullWidthInput}
            min={0}
            step={0.0001}
            stringMode
            prefix={symbolFor(details?.targetCurrency)}
            placeholder={
              marketRate != null ? formatRateValue(marketRate) : undefined
            }
          />
        </Form.Item>

        {marketRate != null && (
          <Button
            type="link"
            className={styles.inlineLink}
            onClick={() => form.setFieldsValue({rate: marketRate})}>
            Use the market rate ({formatRateValue(marketRate)})
          </Button>
        )}

        <FileUploadList
          form={form}
          name="files"
          label="Supporting files (optional)"
          transactionId={details?.id}
          forType="SWAP"
          refetch={refetch}
          bucket={receiptBucket}
          required={false}
        />

        <div className={styles.modalFooter}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            className={styles.primaryButton}>
            Continue
          </Button>
        </div>
      </Form>
    </div>
  );
};

UpdateRateContent.propTypes = {
  details: PropTypes.object,
  onDone: PropTypes.func,
  onCancel: PropTypes.func,
  refetch: PropTypes.func,
};

const SwapModal = ({updateModal, setUpdateModal, refetch}) => {
  const close = () => setUpdateModal({});

  return (
    <>
      <DBModal
        isOpen={!!updateModal?.open}
        handleClose={close}
        destroyOnClose
        width={560}
        content={
          <FulfilSwapContent
            details={updateModal}
            refetch={refetch}
            onCancel={close}
            onDone={close}
          />
        }
      />
      <DBModal
        isOpen={!!updateModal?.updateOpen}
        handleClose={close}
        destroyOnClose
        width={560}
        content={
          <UpdateRateContent
            details={updateModal}
            refetch={refetch}
            onCancel={close}
            onDone={close}
          />
        }
      />
    </>
  );
};

SwapModal.propTypes = {
  updateModal: PropTypes.object,
  setUpdateModal: PropTypes.func,
  refetch: PropTypes.func,
};

export default SwapModal;
